import { useState, useEffect } from 'react'
import { Card, Button, Input, Modal, Table, Loading, Badge, Alert } from '../components/common'
import { masterService } from '../services/masterService'
import { Trash2 } from 'lucide-react'

function RacksPage() {
  const [racks, setRacks] = useState([])
  const [loading, setLoading] = useState(true)
  const [showModal, setShowModal] = useState(false)
  const [editing, setEditing] = useState(null)
  const [formData, setFormData] = useState({ kode: '', nama: '', lokasi: '', kapasitas: '' })
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [deleteConfirm, setDeleteConfirm] = useState(null) // { id, kode, nama }

  useEffect(() => { fetchData() }, [])

  const fetchData = async () => {
    try {
      setLoading(true)
      const response = await masterService.getRacks()
      if (response.success) setRacks(response.data.racks || [])
    } catch (err) { console.error(err) }
    finally { setLoading(false) }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!formData.kode || !formData.nama) { setError('Kode dan nama harus diisi'); return }
    setSaving(true)
    try {
      const response = editing
        ? await masterService.updateRack(editing.id, { ...formData, kapasitas: parseInt(formData.kapasitas) || 0 })
        : await masterService.createRack({ ...formData, kapasitas: parseInt(formData.kapasitas) || 0 })
      if (response.success) { setShowModal(false); setEditing(null); fetchData() }
      else { setError(response.message) }
    } catch (err) { setError('Gagal menyimpan') }
    finally { setSaving(false) }
  }

  const handleEdit = (item) => {
    setEditing(item)
    setFormData({ kode: item.kode, nama: item.nama, lokasi: item.lokasi || '', kapasitas: item.kapasitas || '' })
    setShowModal(true)
  }

  const handleDeleteClick = (rack) => {
    setDeleteConfirm({ id: rack.id, kode: rack.kode, nama: rack.nama })
  }

  const handleDeleteConfirm = async () => {
    if (!deleteConfirm) return
    try {
      await masterService.deleteRack(deleteConfirm.id)
      setDeleteConfirm(null)
      fetchData()
    } catch (err) {
      alert(err.response?.data?.message || 'Gagal menghapus')
      setDeleteConfirm(null)
    }
  }

  const columns = [
    { header: 'Kode', render: (row) => <span className="font-mono font-bold">{row.kode}</span> },
    { header: 'Nama', accessor: 'nama' },
    { header: 'Lokasi', accessor: 'lokasi' },
    { header: 'Kapasitas', render: (row) => row.kapasitas?.toLocaleString() },
    { header: 'Status', render: (row) => <Badge variant={row.status === 'active' ? 'success' : 'default'}>{row.status}</Badge> },
    { header: 'Aksi', render: (row) => (
      <div className="flex gap-2">
        <button onClick={() => handleEdit(row)} className="text-blue-600 hover:underline text-sm">Edit</button>
        <button onClick={() => handleDeleteClick(row)} className="text-red-600 hover:underline text-sm">Hapus</button>
      </div>
    )}
  ]

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div><h1 className="text-2xl font-bold">🗄️ Master Rak Gudang</h1><p className="text-gray-500">Kelola rak penyimpanan gudang</p></div>
        <Button onClick={() => { setEditing(null); setFormData({ kode: '', nama: '', lokasi: '', kapasitas: '' }); setShowModal(true) }}>➕ Tambah Rak</Button>
      </div>
      <Card>{loading ? <Loading /> : <Table columns={columns} data={racks} emptyText="Belum ada rak" />}</Card>
      <Modal isOpen={showModal} onClose={() => setShowModal(false)} title={editing ? 'Edit Rak' : 'Tambah Rak'}
        footer={<><Button variant="secondary" onClick={() => setShowModal(false)}>Batal</Button><Button onClick={handleSubmit} loading={saving}>Simpan</Button></>}>
        {error && <Alert type="error" className="mb-4">{error}</Alert>}
        <form onSubmit={handleSubmit} className="space-y-4">
          <Input label="Kode *" value={formData.kode} onChange={(e) => setFormData(p => ({ ...p, kode: e.target.value }))} disabled={!!editing} placeholder="RAK-A1" />
          <Input label="Nama *" value={formData.nama} onChange={(e) => setFormData(p => ({ ...p, nama: e.target.value }))} placeholder="Rak A1" />
          <Input label="Lokasi" value={formData.lokasi} onChange={(e) => setFormData(p => ({ ...p, lokasi: e.target.value }))} placeholder="Lantai 1" />
          <Input label="Kapasitas" type="number" value={formData.kapasitas} onChange={(e) => setFormData(p => ({ ...p, kapasitas: e.target.value }))} placeholder="1000" />
        </form>
      </Modal>

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 max-w-sm mx-4 shadow-2xl">
            <div className="text-center">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Trash2 className="w-6 h-6 text-red-600" />
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Hapus Rak?</h3>
              <p className="text-gray-600 mb-1">
                Anda yakin ingin menghapus rak:
              </p>
              <p className="font-medium text-gray-900 mb-4">
                {deleteConfirm.kode} - {deleteConfirm.nama}
              </p>
              <p className="text-sm text-red-600 mb-6">
                Tindakan ini tidak dapat dibatalkan.
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setDeleteConfirm(null)}
                  className="flex-1 py-2 border rounded-lg text-gray-700 hover:bg-gray-100 font-medium"
                >
                  Batal
                </button>
                <button
                  onClick={handleDeleteConfirm}
                  className="flex-1 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium"
                >
                  Ya, Hapus
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default RacksPage
